import React, { useState, useEffect, useLayoutEffect } from "react";
import Card from "@common/Card";
import Modal from "@modals/Modal";
import Popup from "@modals/Popup";
import SalePopup from "@modals/SalePopup";
import PiePopup from "@modals/PiePopup";
import { fundRaisin } from "@contracts/fundraisin";
import converter from "number-to-words";
import { logError, logInfo } from "@utils/log";
import Spinner from "@common/Spinner";
import Loader from "@common/Loader";
import error from "@config/error.json";
import ShareModal from "@modals/ShareModal";
import Pagination from "@common/Pagination";
import constant from "@utils/constant";
import { useMemo } from "react";
import { useAppContext } from "@context/AppContext";

const MyPage = () => {
  const {
    themeConfig,
    siteDetails,
    walletAddress,
    connectWallet,
    isWalletConnected,
    nftData,
    newest,
    loading,
    refreshNftData,
    setError
  } = useAppContext();
  const [currentPage, setPage] = useState(1);
  const [saleShow, setSaleShow] = useState(false);
  const [pieShow, setPieShow] = useState(false);
  const [saleStatus, setSaleStatus] = useState("");
  const [card, setCard] = useState({});
  const [priceDist, setPrice] = useState(0);
  const [spinner, setSpinner] = useState(false);
  const [showShare, setShareShow] = useState(false);
  const [popUpCard, setPopUpCard] = useState(0);
  const slider = siteDetails.sliders;

  const paginationData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * constant.MYCARDS_PER_PAGE;
    const lastPageIndex = firstPageIndex + constant.MYCARDS_PER_PAGE;
    logInfo(
      nftData.length,
      firstPageIndex,
      lastPageIndex,
      nftData,
      nftData.slice(firstPageIndex, lastPageIndex)
    );
    return nftData.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, nftData]);

  useEffect(() => {
    setPage(1);
  }, [walletAddress]);

  const downloadImage = (card) => {
    logInfo(card);
    fetch(`/download`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify({ url: card.image }),
    }).then(function (res) {
      logInfo(res);
      res.blob().then((blob) => {
        let url = window.URL.createObjectURL(blob);
        logInfo(url);
        let a = document.createElement("a");
        a.href = url;
        a.download = card.name + ".png";
        a.click();
      });
    });
  };

  useLayoutEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  });

  const openSaleModal = (card, btn) => {
    logInfo({ card, btn });
    setCard(card);
    setSaleStatus(btn);
    setSaleShow(!saleShow);
    setPrice(card.originalPrice);
  };

  const closeSaleModal = () => {
    setSaleShow(!saleShow);
  };

  const openShareModal = (card) => {
    setCard(card);
    setShareShow(!showShare);
  };

  const closeShareModal = () => {
    setShareShow(!showShare);
  };

  const openPieModal = (card) => {
    setPopUpCard(card);
    setPieShow(!pieShow);
  };

  const closePieModal = () => {
    setPieShow(!pieShow);
  };

  const renderPiePopup = () => {
    return (
      <Modal show={pieShow}>
        <Popup>
          <PiePopup card={popUpCard} sliders={slider} onClose={closePieModal} />
        </Popup>
      </Modal>
    );
  };

  const cancelSale = async () => {
    setSpinner(true);
    const tokenId = card.tokenId;
    const siteTokenIndex = card.siteTokenIndex;
    const price = card.originalPrice.toString();
    let status = isWalletConnected;
    if (!isWalletConnected) {
      status = await connectWallet();
    }
    if (status) {
      await fundRaisin.changeTokenPrice(
        siteDetails.siteId,
        siteTokenIndex,
        tokenId,
        price,
        false
      );
      setSpinner(false);
      setSaleShow(!saleShow);
      refreshNftData();
    } else {
      setSpinner(false);
    }
  };

  const placeSale = async () => {
    setSpinner(true);
    const tokenId = card.tokenId;
    const siteTokenIndex = card.siteTokenIndex;
    const price = priceDist.toString();
    let status = isWalletConnected;
    if (!isWalletConnected) {
      status = await connectWallet();
    }
    if (status) {
      fundRaisin
        .changeTokenPrice(
          siteDetails.siteId,
          siteTokenIndex,
          tokenId,
          price,
          true
        )
        .then((message) => {
          setSpinner(false);
          setSaleShow(!saleShow);
          refreshNftData();
        })
        .catch((err) => {
          logError(err);
          setSpinner(false);
          const errorMsg = parseErrorMessage(err, themeConfig, 'sell');
          setError(errorMsg);
        });
    } else {
      setSpinner(false);
    }
  };

  const changeDist = (value) => {
    setPrice(value);
  };

  const renderSalePopup = () => {
    if (Object.keys(card).length > 0) {
      return (
        <Modal show={saleShow}>
          <Popup>
            <SalePopup
              sliders={siteDetails.sliders}
              card={card}
              show={saleShow}
              saleStatus={saleStatus}
              onClose={closeSaleModal}
              onCancel={cancelSale}
              onSale={placeSale}
              onChangePrice={changeDist}
            />
          </Popup>
        </Modal>
      );
    }
  };

  const renderSharePopup = () => {
    if (Object.keys(card).length > 0) {
      return (
        <Modal show={showShare}>
          <Popup>
            <ShareModal
              card={card}
              onDownload={downloadImage}
              onClose={closeShareModal}
            />
          </Popup>
        </Modal>
      );
    }
  };

  const renderCardData = () => {
    if (paginationData.length === 0 && !loading)
      return <h3 className="sort__text">No item to display</h3>;
    return (
      <>
        {paginationData.map((card) => (
          <div className="nft-grid-card__wrapper" key={card.id}>
            <Card
              card={card}
              btn={card.forSale ? "Cancel Sale" : "Sell"}
              show={saleShow}
              download={true}
              overlay={false}
              overlayText={card.forSale}
              newest={newest}
              onBtnOpen={openSaleModal}
              onPieOpen={() => openPieModal(card)}
              onShare={() => openShareModal(card)}
            />
          </div>
        ))}
      </>
    );
  };

  const getTitle = () => {
    if (nftData.length) {
      if (nftData.length === 1) {
        return `My One ${themeConfig.APP_NFT_NAME}`;
      } else {
        return `My ${nftData.length > 12
          ? nftData.length
          : converter.toWords(nftData.length)
          } ${themeConfig.APP_NFT_PLURAL}`;
      }
    }
    return `My ${themeConfig.APP_NFT_NAME}`;
  };

  return (
    <section id="mynft-page" className="page-settings">
      <Spinner spinner={spinner} />
      <div className="mynft-page__title-box row">
        <div className="col-12">
          <h1 className="mynft-page__title title-styles">{getTitle()}</h1>
        </div>
      </div>
      {renderSalePopup()}
      <div
        className="nft-cards buy-cards"
        style={{ justifyContent: nftData.length < 4 ? "center" : "" }}
      >
        {renderCardData()}
        {<Loader loading={loading} />}
      </div>
      <Pagination
        currentPage={currentPage}
        totalCount={nftData.length}
        pageSize={constant.MYCARDS_PER_PAGE}
        onPageChange={(page) => setPage(page)}
      />
      {renderPiePopup()}
      {renderSharePopup()}
    </section>
  );
};

export default MyPage;
